import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';

interface Facility {
  id: string;
  name: string;
  available: boolean;
  price: number;
  notice: string;
  operatingHours: {
    start: number;
    end: number;
  };
  operatingDays: number[];
  closedPeriods?: Array<{
    id: string;
    start: {
      date: string;
      time: number;
    };
    end: {
      date: string;
      time: number;
    };
  }>;
  imageUrl?: string;
  reservationDays: number;
  reservationTimeUnit: number;
  reservationType: string;
}

const GuidePage: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const q = query(
          collection(db, 'facilities'),
          where('available', '==', true),
          orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);
        
        const facilitiesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name || '',
            available: data.available ?? true,
            price: data.price || 0,
            notice: data.notice || '',
            operatingHours: data.operatingHours || { start: 9, end: 18 },
            operatingDays: data.operatingDays || [1, 2, 3, 4, 5],
            closedPeriods: data.closedPeriods || [],
            imageUrl: data.imageUrl || '',
            reservationDays: data.reservationDays ?? 0,
            reservationTimeUnit: data.reservationTimeUnit || 1,
            reservationType: data.reservationType || '',
          };
        });

        setFacilities(facilitiesData);
      } catch (error) {
        console.error('시설물 목록 조회 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, []);

  // 운영 요일을 한국어로 변환하는 함수
  const formatOperatingDays = (days: number[]) => {
    const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    return days.map(day => dayNames[day]).join(', ');
  };

  // 시간을 포맷팅하는 함수
  const formatTime = (hour: number) => {
    return `${String(hour).padStart(2, '0')}:00`;
  };

  return (
    <div className="w-full min-h-screen bg-slate-50">
      {/* Header */}
      <div className="w-full h-20 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.01)]">
        <div className="max-w-[1920px] mx-auto px-4 h-full relative flex items-center">
          <Link to="/" className="md:block hidden">
            <div className="w-auto h-8 left-[40px] top-[26px] absolute whitespace-nowrap text-[#1A253F] text-[14px] md:text-[28px] font-extrabold font-pretendard leading-9">
              부산항신항시설관리센터
            </div>
          </Link>
          {/* Mobile Header */}
          <Link to="/" className="md:hidden block w-full">
            <div className="w-full h-7 text-center text-[#1A253F] text-[18px] md:text-[28px] font-extrabold font-pretendard leading-9">
              부산항신항시설관리센터
            </div>
          </Link>
        </div>
      </div>
      <div className="w-full h-px bg-white border border-slate-200"></div>

      {/* Main Content */}
      <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-16 py-6 md:py-12">
        <div className="w-[calc(100%-32px)] md:w-[760px] mx-auto relative">
          {/* 헤더 섹션 */}
          <div className="mt-4 md:mt-8">
            <div className="text-slate-800 text-2xl md:text-4xl font-extrabold font-['Pretendard'] leading-9">
              이용안내
            </div>
            <div className="mt-4">
              <div className="text-slate-700 text-lg md:text-xl font-normal font-['Pretendard'] leading-9">
                시설물 이용 정보를 확인하실 수 있습니다
              </div>
            </div>
          </div>

        {/* 구분선 */}
        <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

        <div className="mt-6 md:mt-8 space-y-6 md:space-y-8">
          {/* 시설물 이용정보 섹션 */}
          <div>
            <div className="text-slate-800 text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              시설물 이용정보
            </div>
            <div className="bg-white p-4 md:p-8 rounded border border-black/5">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-slate-600 text-lg font-normal font-['Pretendard']">
                    시설물 정보를 불러오는 중...
                  </div>
                </div>
              ) : facilities.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-slate-600 text-lg font-normal font-['Pretendard']">
                    현재 이용 가능한 시설물이 없습니다.
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:gap-6">
                  {facilities.map((facility) => (
                    <div
                      key={facility.id}
                      className="p-4 md:p-6 rounded-sm outline outline-[0.50px] outline-gray-300 bg-white"
                    >
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-start mb-4 gap-3 md:gap-0">
                        <div className="flex-1">
                          <h3 className="text-slate-800 text-base md:text-lg font-bold font-['Pretendard'] mb-2">
                            {facility.name}
                          </h3>
                          <p className="text-slate-700 text-sm md:text-base font-normal font-['Pretendard']">
                            이용료: {facility.price.toLocaleString()}원
                          </p>
                        </div>
                        {facility.imageUrl && (
                          <img
                            src={facility.imageUrl}
                            alt={facility.name}
                            className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-sm"
                          />
                        )}
                      </div>

                      {/* 상세 정보 */}
                      <div className="space-y-2 md:space-y-3 mt-4">
                        <div className="flex items-start md:items-center">
                          <span className="text-slate-600 text-xs md:text-sm font-medium font-['Pretendard'] w-20 md:w-24 flex-shrink-0">
                            운영시간:
                          </span>
                          <span className="text-slate-800 text-xs md:text-sm font-normal font-['Pretendard']">
                            {formatTime(facility.operatingHours.start)} - {formatTime(facility.operatingHours.end)}
                          </span>
                        </div>

                        <div className="flex items-start md:items-center">
                          <span className="text-slate-600 text-xs md:text-sm font-medium font-['Pretendard'] w-20 md:w-24 flex-shrink-0">
                            운영요일:
                          </span>
                          <span className="text-slate-800 text-xs md:text-sm font-normal font-['Pretendard']">
                            {formatOperatingDays(facility.operatingDays)}
                          </span>
                        </div>

                        <div className="flex items-start md:items-center">
                          <span className="text-slate-600 text-xs md:text-sm font-medium font-['Pretendard'] w-20 md:w-24 flex-shrink-0">
                            예약방식:
                          </span>
                          <span className="text-slate-800 text-xs md:text-sm font-normal font-['Pretendard']">
                            {facility.reservationType === 'time-slot' ? '시간대별 예약' : '시작/종료 시간 선택'}
                          </span>
                        </div>

                        <div className="flex items-start md:items-center">
                          <span className="text-slate-600 text-xs md:text-sm font-medium font-['Pretendard'] w-20 md:w-24 flex-shrink-0">
                            예약단위:
                          </span>
                          <span className="text-slate-800 text-xs md:text-sm font-normal font-['Pretendard']">
                            {facility.reservationTimeUnit}시간
                          </span>
                        </div>

                        <div className="flex items-start">
                          <span className="text-slate-600 text-xs md:text-sm font-medium font-['Pretendard'] w-20 md:w-24 flex-shrink-0">
                            예약가능:
                          </span>
                          <span className="text-slate-800 text-xs md:text-sm font-normal font-['Pretendard']">
                            {facility.reservationDays === 0
                              ? '당일 예약 가능'
                              : `${facility.reservationDays}일 후부터 예약 가능`}
                          </span>
                        </div>

                        {facility.notice && (
                          <div className="mt-3 md:mt-4 p-3 md:p-4 bg-blue-50 rounded-sm border-l-4 border-blue-400">
                            <div className="flex items-start">
                              <span className="text-blue-700 text-xs md:text-sm font-medium font-['Pretendard'] flex-shrink-0 mr-2">
                                안내사항:
                              </span>
                              <span className="text-blue-800 text-xs md:text-sm font-normal font-['Pretendard']">
                                {facility.notice}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* 휴무 기간이 있는 경우 표시 */}
                        {facility.closedPeriods && facility.closedPeriods.length > 0 && (
                          <div className="mt-3 md:mt-4 p-3 md:p-4 bg-red-50 rounded-sm border-l-4 border-red-400">
                            <div className="text-red-700 text-xs md:text-sm font-medium font-['Pretendard'] mb-2">
                              휴무 기간:
                            </div>
                            {facility.closedPeriods.map((period, index) => (
                              <div key={index} className="text-red-800 text-xs md:text-sm font-normal font-['Pretendard']">
                                {period.start.date} ~ {period.end.date}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 예약 안내 섹션 */}
          <div>
            <div className="text-slate-800 text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              예약 이용 안내
            </div>
            <div className="bg-white p-4 md:p-8 rounded border border-black/5">
              <div className="space-y-3 md:space-y-4 text-slate-700 text-sm md:text-base font-normal font-['Pretendard'] leading-relaxed">
                <div className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">•</span>
                  <span>시설물 예약은 로그인 후 이용 가능합니다.</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">•</span>
                  <span>예약 승인은 관리자 검토 후 확정됩니다.</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">•</span>
                  <span>예약시 시설물에 따라 비용이 청구될 수 있습니다.</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">•</span>
                  <span>시설물 이용 시 안전 수칙을 준수해 주시기 바랍니다.</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">•</span>
                  <span>문의사항은 051-890-7455으로 연락 주시기 바랍니다.</span>
                </div>
              </div>
              
              <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
                <Link
                  to="/facility"
                  className="inline-block w-full h-14 md:h-16 bg-blue-500 hover:bg-blue-600 rounded-sm transition-colors"
                >
                  <div className="text-center text-white text-base md:text-lg font-bold font-['Pretendard'] leading-[56px] md:leading-[64px]">
                    시설물 예약하기
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16 md:h-32"></div>
        </div>
      </div>

      {/* 푸터 */}
      <footer className="bg-[#0E2039] h-[340px] md:h-[210px] mt-[100px]">
        <div className="max-w-[1380px] mx-auto px-4 sm:px-8 md:px-16 pt-[40px] md:pt-[54px]">
          <div className="flex flex-col md:flex-row justify-between">
            <div>
              <h4 className="text-[16px] md:text-[24px] font-semibold text-white/80 font-pretendard mb-[41px]">
                부산항신항시설관리센터
              </h4>
              <div className="flex flex-col md:flex-row items-start md:items-center gap-[8px] md:gap-[24px]">
                <p className="text-[14px] md:text-[15px] font-medium text-[#64AAC6] font-pretendard">Contact</p>
                <p className="text-[12px] font-light text-white/40 font-pretendard">
                  부산광역시 강서구 신항로 96-87
                </p>
                <div className="flex items-center gap-[8px]">
                  <p className="text-[12px] font-light text-white/40 font-pretendard">
                    대표번호:051-890-7400
                  </p>
                  <p className="text-[12px] font-light text-white/40 font-pretendard">
                    FAX:051-831-5344
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-[8px] md:gap-[44px] mt-[20px] md:mt-0">
              <Link to="/notices" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">공지사항</Link>
              <Link to="/privacy" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">개인정보처리방침</Link>
              <Link to="/email-policy" className="text-[14px] md:text-[15px] font-bold text-white font-pretendard">이메일무단수집거부</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GuidePage; 